import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Flame, LogOut, Loader2, RefreshCw, Download, Trophy, ShieldCheck,
  CheckCircle2, Clock, ExternalLink, Lock, Users, Ticket,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/hooks/useGiveawayStats";
import { GIVEAWAY } from "@/data/giveaway";

const TOKEN_KEY = "tgb_admin_token";
const short = (s, n = 4) => (s ? `${s.slice(0, n)}...${s.slice(-n)}` : "");

const StatusBadge = ({ status }) => {
  const map = {
    verified: { c: "text-green-400 border-green-400/40", i: CheckCircle2, t: "Verified" },
    pending: { c: "text-yellow-400 border-yellow-400/40", i: Clock, t: "Pending" },
  };
  const s = map[status] || map.pending;
  const Icon = s.i;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-body font-bold uppercase tracking-wide border rounded-full px-2 py-0.5 ${s.c}`}>
      <Icon className="w-3 h-3" /> {s.t}
    </span>
  );
};

// ---------------- Login ----------------
const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Welcome back, boss 🔥");
      onLogin(data.token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const input = "w-full bg-black/50 border border-white/10 rounded-md px-4 py-3 font-body text-sm text-white placeholder:text-gray-500 focus:border-gold/60 focus:outline-none transition-colors duration-300";

  return (
    <div className="min-h-screen flex items-center justify-center px-5 bg-black">
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        onSubmit={submit}
        className="glass rounded-lg p-8 w-full max-w-md"
        data-testid="admin-login-form"
      >
        <div className="flex items-center gap-3 mb-6">
          <img src={GIVEAWAY.logo} alt="TGB" className="w-12 h-12 rounded-full" />
          <div>
            <p className="font-display text-xl uppercase text-white flex items-center gap-2"><Lock className="w-4 h-4 text-gold" /> Admin</p>
            <p className="font-body text-xs text-gray-400">The Greater Bull Giveaway</p>
          </div>
        </div>
        <div className="space-y-4">
          <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={input} data-testid="admin-email" required />
          <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={input} data-testid="admin-password" required />
        </div>
        <button type="submit" disabled={loading} data-testid="admin-login-submit" className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-hover disabled:opacity-60 text-black font-body font-extrabold px-8 py-3.5 rounded-md transition-colors duration-300 uppercase tracking-wide">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Flame className="w-5 h-5" /> Sign In</>}
        </button>
      </motion.form>
    </div>
  );
};

// ---------------- Dashboard ----------------
const Dashboard = ({ token, onLogout }) => {
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [draw, setDraw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawing, setDrawing] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    try {
      const [s, e, d] = await Promise.all([
        axios.get(`${API}/admin/summary`, auth),
        axios.get(`${API}/admin/entries`, auth),
        axios.get(`${API}/admin/draws`, auth),
      ]);
      setSummary(s.data);
      setEntries(e.data);
      if (d.data?.length) setDraw(d.data[0]);
    } catch (err) {
      if (err?.response?.status === 401) onLogout();
      else toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const reverifyEntry = async (id) => {
    try {
      const { data } = await axios.post(`${API}/admin/entries/${id}/reverify`, {}, auth);
      if (data.ok) toast.success(`Verified on-chain — $${data.verified_usd} confirmed`);
      else toast.error(data.detail || "Could not verify on-chain");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Re-verify failed (RPC unavailable)");
    }
  };

  const forceVerify = async (id) => {
    try {
      await axios.post(`${API}/admin/entries/${id}/verify`, {}, auth);
      toast.success("Entry manually marked verified");
      load();
    } catch { toast.error("Failed to verify"); }
  };

  const runDraw = async () => {
    setDrawing(true);
    try {
      const { data } = await axios.post(`${API}/admin/draw`, {}, auth);
      setDraw(data);
      toast.success("Winners drawn! 🏆");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Draw failed");
    } finally { setDrawing(false); }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/export`, { ...auth, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "tgb_giveaway_entries.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-black"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>;
  }

  const stats = [
    { label: "Raised (verified)", value: `$${(summary?.raised_usd || 0).toLocaleString()}`, icon: Flame, color: "text-gold" },
    { label: "Verified Tickets", value: summary?.verified_count ?? 0, icon: Ticket, color: "text-green-400" },
    { label: "Pending Review", value: summary?.pending_count ?? 0, icon: Clock, color: "text-yellow-400" },
    { label: "Participants", value: summary?.unique_participants ?? 0, icon: Users, color: "text-solana" },
  ];

  return (
    <div className="min-h-screen bg-black" data-testid="admin-dashboard">
      <header className="glass border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={GIVEAWAY.logo} alt="TGB" className="w-9 h-9 rounded-full" />
            <span className="font-display text-lg uppercase text-white">Giveaway Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} data-testid="admin-refresh" className="p-2 text-gray-400 hover:text-gold transition-colors duration-300"><RefreshCw className="w-5 h-5" /></button>
            <button onClick={exportCsv} data-testid="admin-export" className="inline-flex items-center gap-2 border border-white/15 text-white hover:border-gold hover:text-gold font-body font-bold text-sm px-4 py-2 rounded-md transition-colors duration-300">
              <Download className="w-4 h-4" /> <span className="hidden sm:inline">Export CSV</span>
            </button>
            <button onClick={onLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-gray-400 hover:text-blood font-body font-bold text-sm px-3 py-2 transition-colors duration-300">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-8 space-y-8">
        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s, i) => (
            <div key={i} className="glass rounded-lg p-5" data-testid={`admin-stat-${i}`}>
              <s.icon className={`w-6 h-6 mb-3 ${s.color}`} />
              <p className="font-display text-3xl text-white leading-none">{s.value}</p>
              <p className="font-body text-xs uppercase tracking-widest text-gray-400 mt-2">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Progress */}
        <div className="glass rounded-lg p-6">
          <div className="flex items-end justify-between mb-3">
            <span className="font-body text-sm font-bold uppercase tracking-widest text-gold">Goal Progress</span>
            <span className="font-body text-sm text-gray-400">{summary?.percent ?? 0}% of ${(summary?.goal_usd || 0).toLocaleString()}</span>
          </div>
          <div className="h-4 w-full rounded-full bg-white/5 border border-white/10 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-gold via-ember to-blood" style={{ width: `${Math.max(summary?.percent || 0, 1)}%` }} />
          </div>
        </div>

        {/* Draw */}
        <div className="glass rounded-lg p-6" data-testid="admin-draw-panel">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <div>
              <h2 className="font-display text-2xl uppercase text-white flex items-center gap-2"><Trophy className="w-5 h-5 text-gold" /> Winner Draw</h2>
              <p className="font-body text-sm text-gray-400 mt-1">Weighted by entries across verified participants.</p>
            </div>
            <button onClick={runDraw} disabled={drawing} data-testid="admin-run-draw" className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-gold to-ember disabled:opacity-60 text-black font-body font-extrabold text-sm px-6 py-3 rounded-md uppercase tracking-wide hover:scale-105 transition-transform duration-300">
              {drawing ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Trophy className="w-4 h-4" /> Run Draw</>}
            </button>
          </div>
          {draw && (
            <div className="grid sm:grid-cols-3 gap-4" data-testid="admin-draw-results">
              {draw.winners.map((w, i) => (
                <div key={i} className="rounded-md border border-gold/30 bg-gold/5 p-4">
                  <p className="font-body text-[11px] uppercase tracking-widest text-gold">{w.prize}</p>
                  <p className="font-body text-white font-bold mt-1 break-all">{w.email}</p>
                  <p className="font-body text-xs text-gray-400 mt-1">{w.entries} entries</p>
                </div>
              ))}
              <p className="sm:col-span-3 font-body text-xs text-gray-500">Drawn {new Date(draw.drawn_at).toLocaleString()} · {draw.total_participants} participants · {draw.total_entries_weight} total entries</p>
            </div>
          )}
        </div>

        {/* Entries table */}
        <div className="glass rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-white/10 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-gold" />
            <h2 className="font-display text-xl uppercase text-white">Entries ({entries.length})</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="admin-entries-table">
              <thead>
                <tr className="text-[11px] font-body font-bold uppercase tracking-widest text-gray-500 border-b border-white/10">
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Ticket</th>
                  <th className="px-4 py-3">Paid</th>
                  <th className="px-4 py-3">Entries</th>
                  <th className="px-4 py-3">TX</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-10 text-center font-body text-gray-500">No entries yet.</td></tr>
                )}
                {entries.map((e) => (
                  <tr key={e.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`admin-entry-${e.id}`}>
                    <td className="px-4 py-3"><StatusBadge status={e.status} /></td>
                    <td className="px-4 py-3 font-body text-sm text-white">{e.email}</td>
                    <td className="px-4 py-3 font-body text-sm text-gray-300">{e.ticket_name} <span className="text-gray-500">(${e.amount_usd})</span></td>
                    <td className="px-4 py-3 font-body text-sm text-gray-300">{e.currency_amount ? `${e.currency_amount} ${e.currency}` : `— ${e.currency}`}</td>
                    <td className="px-4 py-3 font-body text-sm text-gold font-bold">{e.entries}x</td>
                    <td className="px-4 py-3">
                      <a href={`https://solscan.io/tx/${e.tx_hash}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-mono text-xs text-solana hover:text-gold transition-colors duration-300">
                        {short(e.tx_hash, 5)} <ExternalLink className="w-3 h-3" />
                      </a>
                    </td>
                    <td className="px-4 py-3 font-body text-xs text-gray-400">{new Date(e.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      {e.status === "pending" && (
                        <div className="flex items-center gap-3">
                          <button onClick={() => reverifyEntry(e.id)} data-testid={`admin-reverify-${e.id}`} className="text-xs font-body font-bold uppercase tracking-wide text-gold hover:text-gold-hover transition-colors duration-300">Re-verify</button>
                          <button onClick={() => forceVerify(e.id)} data-testid={`admin-verify-${e.id}`} className="text-xs font-body font-bold uppercase tracking-wide text-green-400 hover:text-green-300 transition-colors duration-300">Force</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <Login onLogin={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
